import {
  Action,
  createPickSelector,
  createSelector, Selector,
  State, StateContext,
  StateToken, Store
} from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { AuthStateModel, LogoutAction, SetUserAndAuthStatus } from './core/authentication/auth.state.model';
import { AuthState } from './core/authentication/auth.state';
import { AppActions } from './app.actions';
import { TranslationService } from './core/services/translationService';
import { environment } from '../environments/environment';
import { ConsumerEntity } from './consumer/consumer.entity';
import { ConsumerEntityService } from './consumer/services/consumer.entity.service';
import { SubjectEntity } from './subject/subject.entity';
import { SubjectEntityService } from './subject/services/subject.entity.service';

export class SetIsLoaded {
  static readonly type = '[App] Set Is Loaded';
  constructor(public isLoaded: boolean) { }
}

const appStateToken = new StateToken<AppStateModel>('appState');

export interface AppStateModel {
  readonly isLoaded: boolean
  readonly consumers: ConsumerEntity[]
  readonly subjects: SubjectEntity[]
}

@State<AppStateModel>({
  name: appStateToken,
  defaults: {
    isLoaded: false,
    consumers: [],
    subjects: []
  }
})
@Injectable()
export class AppState {
  private store = inject(Store);
  private translationService = inject(TranslationService);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);

  @Selector([AuthState])
  static isAuthenticated(authState: AuthStateModel): boolean {
    return authState.isAuthenticated;
  }

  @Selector([AuthState])
  static userRole(authState: AuthStateModel): string {
    return authState.editor?.role || '';
  }

  @Selector()
  static isLoaded(state: AppStateModel) {
    return state.isLoaded;
  }

  @Action(AppActions.RestoreAuthState)
  restoreAuthState(ctx: StateContext<AppStateModel>) {
    const isAuthenticated = localStorage.getItem('isAuthenticated') !== null;
    const editor = JSON.parse(localStorage.getItem('editor') || 'null');

    // Restore Auth-State first
    this.store.dispatch(new SetUserAndAuthStatus({ isAuthenticated, userRole: editor?.role }));

    if (isAuthenticated) {
      this.store.dispatch(new AppActions.LoadInitialData());
    }
    else {
      ctx.patchState({ isLoaded: true });
    }
  }

  @Action(AppActions.LoadInitialData)
  loadInitialData(ctx: StateContext<AppStateModel>) {
    if (!environment.isProd) {
      console.log('::==> Loading initial data... <==::');
    }

    if (localStorage.getItem('access_token') !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this.loadTranslations((t) => {
        this.loadConsumers(async (consumers) => {
          if (!environment.isProd) {
            console.log('::==> Consumers loaded <==::');
          }
          ctx.patchState({ consumers });
          this.loadSubjects(async (subjects) => {
            if (!environment.isProd) {
              console.log('::==> Subjects loaded <==::');
            }
            ctx.patchState({
              subjects, // Sorting is handled by the service selector
              isLoaded: true
            });
          });
        });
      });
    }
  }

  @Action(LogoutAction)
  onLogout(ctx: StateContext<AppStateModel>) {
    ctx.patchState({ isLoaded: false, consumers: [], subjects: [] });
  }

  @Action(AppActions.StartupAfterLogin)
  onStartupAfterLogin(ctx: StateContext<AppStateModel>, action: AppActions.StartupAfterLogin) {
    console.log(`::==> Startup after login, newLogin ? ${action.isNewLogin} <==::`);

    this.loadTranslations((t) => {
      console.log(`::==> Translations loaded ${t} <==::`);
      this.loadConsumers(async (consumers) => {
        ctx.patchState({ consumers });
        await this.loadSubjects(async (subjects) => {
          ctx.patchState({
            subjects,
            isLoaded: true
          });
        });
      });
    });
  }

  private loadTranslations(callback: (result: Record<string, string>) => void) {
    this.translationService.loadTranslations().subscribe(translations => {
      this.translationService.setTranslations(translations);
      callback(translations);
    });
  }

  private async loadConsumers(callback: (result: ConsumerEntity[]) => void) {
    const query = { sort: 'name' };
    await this.consumerService.ensureLoaded(query);
    const consumers = this.store.selectSnapshot(this.consumerService.allByQuery(query));
    callback(consumers);
  }

  private async loadSubjects(callback: (result: SubjectEntity[]) => void) {
    const query = { sort: 'name' };
    await this.subjectService.ensureLoaded(query);
    const subjects = this.store.selectSnapshot(this.subjectService.allByQuery(query));
    callback(subjects);
  }
}

export class AppSelectors {
  static getFullAppState = createSelector([AppState], (state: AppStateModel) => state);
  static getFullAuthState = createSelector([AuthState], (state: AuthStateModel) => state);

  static authState = createPickSelector(this.getFullAuthState, [
    'isAuthenticated',
    'editor'
  ]);

  static appState = createPickSelector<AppStateModel, (keyof AppStateModel)[]>(this.getFullAppState, [
    'isLoaded',
    'consumers',
    'subjects'
  ]);

  static userRole = createSelector([this.getFullAuthState], (authState: AuthStateModel) => {
    return authState.editor?.role || '';
  });
}
